<template>
  <container :loading="!stateReady">
    <template v-slot:header>
      <h3>Pipeline de Orçamentos</h3>
    </template>
    <template v-slot:body>
      <div>
        <div class="kanban-filters">
          <entity-selector-cliente
            v-if="amIAllowed('view', 'root-cliente')"
            :cliente="undefined"
            :canEdit="true"
            :idClienteFiltered="filter.idCliente"
            @confirm-selection="confirmSelectionCliente"
            :parentLoading="stateLoading"
          />
          <div class="p-formgrid p-grid">
            <div class="p-field p-fluid p-col-12 p-md-6">
              <entity-selector-transportador
                :idTransportadorFiltered="filter.idTransportador ?? undefined"
                :canEdit="true"
                @confirm-selection="confirmSelectionTransportador"
              />
            </div>
            <div class="p-field p-fluid p-col-12 p-md-3">
              <input-with-label label="Origem" v-slot="l">
                <Dropdown
                  v-model="filter.origem"
                  :options="origens"
                  optionLabel="nome"
                  optionValue="codigo"
                  :showClear="true"
                  :id="l.id"
                  :placeholder="'&nbsp;'"
                />
              </input-with-label>
            </div>
            <div class="p-field p-fluid p-col-12 p-md-3">
              <input-with-label label="Mês" v-slot="l">
                <Calendar
                  v-model="filter.dataMesReferencia"
                  :id="l.id"
                  view="month"
                  dateFormat="mm/yy"
                  :showIcon="true"
                  :showButtonBar="true"
                />
              </input-with-label>
            </div>
            <div
              class="p-field p-fluid p-col-12 p-md-3"
              style="display: flex; align-items: flex-end"
            >
              <button-group>
                <Button
                  label="Buscar"
                  class="p-button-rounded"
                  @click="recarregar()"
                />
                <Button
                  label="Limpar"
                  class="p-button-rounded p-button-text"
                  @click="limparFiltros()"
                />
              </button-group>
            </div>
            <div class="p-field p-col-12">
              <div
                style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap"
              >
                <span>
                  <InputSwitch v-model="mostrarArquivados" /> Mostrar arquivados
                  (Recusado / Pausado)
                </span>
                <Button
                  label="Novo orçamento"
                  icon="pi pi-plus"
                  class="p-button-rounded"
                  v-if="amIAllowed('create', 'orcamento')"
                  @click="novoOrcamento()"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="kanban-board" v-if="stateReady">
          <div
            v-for="estagio in estagiosVisiveis"
            :key="estagio.codigo"
            class="kanban-col"
          >
            <div
              class="kanban-col__header"
              :style="{ borderTopColor: estagio.cor }"
            >
              <span class="kanban-col__title" v-text="estagio.nome" />
              <span class="kanban-col__count">
                {{ (colunas[estagio.codigo] || []).length }}
              </span>
            </div>
            <draggable
              :list="colunas[estagio.codigo]"
              group="orcamentos"
              item-key="idOrcamento"
              class="kanban-col__body"
              :data-estagio="estagio.codigo"
              @change="(evt) => onDragChange(evt, estagio.codigo)"
            >
              <template #item="{ element }">
                <card-orcamento
                  :orcamento="element"
                  @click-orcamento="abrirOrcamento"
                />
              </template>
            </draggable>
          </div>
        </div>

        <div v-if="showEdit">
          <cadastro-orcamento
            :idOrcamento="idOrcamentoEdit"
            :key="keyRegister"
            :parentLoading="stateLoading"
            @after-register-screen-closed="afterRegisterScreenClosed"
            @after-save-new-entity="afterSaveNewEntity"
          />
        </div>
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  watch,
  Ref
} from "vue";
import { useRouter, RouteLocationNormalizedLoaded } from "vue-router";
import draggable from "vuedraggable";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast } from "../../modules/message";
import {
  OrcamentoPayload,
  OrcamentoFilterPayload,
  EstagioOrcamento,
  useOrcamentoService
} from "../../modules/business/orcamento/orcamentoBusiness";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import CardOrcamento from "./CardOrcamento.vue";
import CadastroOrcamento from "./CadastroOrcamento.vue";

interface KanbanState {
  filter: OrcamentoFilterPayload;
  colunas: Record<string, Array<OrcamentoPayload>>;
  mostrarArquivados: boolean;
  stateReady: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
  showEdit: boolean;
  idOrcamentoEdit: number | null;
  keyRegister: string;
}

export default defineComponent({
  setup(props) {
    const router = useRouter();
    const orcamentoService = useOrcamentoService();

    const state = reactive<KanbanState>({
      filter: orcamentoService.safeFilterOrcamento(),
      colunas: {},
      mostrarArquivados: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: [],
      showEdit: false,
      idOrcamentoEdit: null,
      keyRegister: ""
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const origens = orcamentoService.getOrigens();

    const estagiosVisiveis = computed<Array<EstagioOrcamento>>(() =>
      orcamentoService.getEstagios(state.mostrarArquivados)
    );

    const inicializarColunas = () => {
      const novas: Record<string, Array<OrcamentoPayload>> = {};
      orcamentoService.getEstagios(true).forEach((e) => {
        novas[e.codigo] = [];
      });
      state.colunas = novas;
    };

    const carregarPagina = (estagio: string, page: number): Promise<void> => {
      const { searchOrcamentos, data, loading } = useOrcamentoService();
      state.loadings.push(loading);
      const filterEstagio = { ...state.filter, estagio };
      return searchOrcamentos(filterEstagio, page, true)
        .then(() => {
          const resp = data.value as {
            content: Array<OrcamentoPayload>;
            page: { totalPages: number };
          };
          state.colunas[estagio].push(...(resp.content ?? []));
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          if (resp.page && page + 1 < resp.page.totalPages) {
            return carregarPagina(estagio, page + 1);
          }
          return undefined;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const recarregar = () => {
      inicializarColunas();
      const promessas: Array<Promise<unknown>> = [];
      orcamentoService.getEstagios(true).forEach((e) => {
        promessas.push(carregarPagina(e.codigo, 0));
      });
      return Promise.all(promessas);
    };

    const limparFiltros = () => {
      state.filter = orcamentoService.safeFilterOrcamento();
      recarregar();
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
      recarregar();
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      state.filter.idTransportador =
        t && t.idTransportador ? t.idTransportador : null;
      recarregar();
    };

    const onDragChange = (
      evt: { added?: { element: OrcamentoPayload; newIndex: number } },
      estagioDestino: string
    ) => {
      if (!evt.added) return;
      const orcamento = evt.added.element;
      const estagioOrigem = orcamento.estagio ?? "";

      if (estagioOrigem === estagioDestino) return;

      if (
        !orcamentoService.isTransicaoArrastavel(estagioOrigem, estagioDestino)
      ) {
        // revert: remover do destino, devolver à origem
        const idx = state.colunas[estagioDestino].findIndex(
          (o) => o.idOrcamento === orcamento.idOrcamento
        );
        if (idx >= 0) {
          state.colunas[estagioDestino].splice(idx, 1);
        }
        state.colunas[estagioOrigem].push(orcamento);

        const orientacao = orcamentoService.getOrientacaoFluxo(estagioOrigem);
        if (
          orientacao &&
          orcamentoService.isTransicaoPermitida(estagioOrigem, estagioDestino)
        ) {
          useToast().warning(orientacao);
        } else {
          useToast().error(
            `Transição inválida: ${estagioOrigem} → ${estagioDestino}`
          );
        }
        return;
      }

      const { updateEstagio, data, loading } = useOrcamentoService();
      state.loadings.push(loading);
      updateEstagio(orcamento.idOrcamento as number, {
        estagio: estagioDestino
      })
        .then(() => {
          const atualizado = data.value as OrcamentoPayload;
          const idx = state.colunas[estagioDestino].findIndex(
            (o) => o.idOrcamento === atualizado.idOrcamento
          );
          if (idx >= 0) {
            state.colunas[estagioDestino][idx] = atualizado;
          }
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          // revert em caso de erro
          const idx = state.colunas[estagioDestino].findIndex(
            (o) => o.idOrcamento === orcamento.idOrcamento
          );
          if (idx >= 0) {
            state.colunas[estagioDestino].splice(idx, 1);
          }
          state.colunas[estagioOrigem].push(orcamento);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const gerarKeyRegister = () => {
      state.keyRegister = state.idOrcamentoEdit
        ? state.idOrcamentoEdit + ""
        : "new-" +
          Math.random()
            .toString(36)
            .substring(2);
    };

    const abrirOrcamento = (orcamento: OrcamentoPayload) => {
      if (orcamento.idOrcamento) {
        router.push({
          name: "orcamento-edit",
          params: { idOrcamento: orcamento.idOrcamento + "" }
        });
      }
    };

    const novoOrcamento = () => {
      router.push({ name: "orcamento-new" });
    };

    const processViewRoute = (
      route: Partial<RouteLocationNormalizedLoaded>
    ) => {
      if (route.name === "orcamento-new") {
        state.idOrcamentoEdit = null;
        state.showEdit = true;
        gerarKeyRegister();
      } else if (route.name === "orcamento-edit") {
        const raw = route.params?.idOrcamento;
        state.idOrcamentoEdit = raw
          ? Array.isArray(raw)
            ? +raw[0]
            : +raw
          : null;
        state.showEdit = true;
        gerarKeyRegister();
      } else {
        state.showEdit = false;
        state.idOrcamentoEdit = null;
      }
    };

    const afterRegisterScreenClosed = () => {
      router.push({ name: "orcamento" });
      recarregar();
    };

    const afterSaveNewEntity = (idOrcamento: number) => {
      router.push({
        name: "orcamento-edit",
        params: { idOrcamento: idOrcamento + "" }
      });
    };

    onMounted(async () => {
      inicializarColunas();
      state.stateReady = true;
      watch([router.currentRoute], () => {
        processViewRoute(router.currentRoute.value);
      });
      processViewRoute(router.currentRoute.value);
      if (props.idOrcamento) {
        state.idOrcamentoEdit = props.idOrcamento;
        state.showEdit = true;
        gerarKeyRegister();
      } else if (props.isNew) {
        state.showEdit = true;
        gerarKeyRegister();
      }
      recarregar();
    });

    return {
      amIAllowed,
      origens,
      estagiosVisiveis,
      stateLoading,
      recarregar,
      limparFiltros,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      onDragChange,
      abrirOrcamento,
      novoOrcamento,
      afterRegisterScreenClosed,
      afterSaveNewEntity,
      ...toRefs(state)
    };
  },
  components: {
    draggable,
    EntitySelectorCliente,
    EntitySelectorTransportador,
    CardOrcamento,
    CadastroOrcamento
  },
  props: {
    idOrcamento: Number,
    isNew: Boolean
  }
});
</script>

<style scoped lang="scss">
.kanban-filters {
  margin-bottom: 1rem;
}

.kanban-board {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  align-items: flex-start;
}

.kanban-col {
  flex: 0 0 280px;
  background: #f5f5f5;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 280px);

  &__header {
    padding: 0.75rem;
    background: #fff;
    border-top: 4px solid #9e9e9e;
    border-radius: 6px 6px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e0e0e0;
  }

  &__title {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.875rem;
    color: #424242;
  }

  &__count {
    background: #e0e0e0;
    border-radius: 12px;
    padding: 0.125rem 0.5rem;
    font-size: 0.75rem;
    color: #424242;
  }

  &__body {
    padding: 0.5rem;
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 80px;
  }
}
</style>
